import {
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/nextjs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "../../ui/dropdown-menu";

import { Button } from "../../ui/button";
import OrganizationButton from "../organization-button";
import { OrganizationAvatar } from "../organization-avatar";
import { PlusCircle } from "lucide-react";

import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import React from "react";
import { useSubscription } from "~/hooks/user-subsciption";
import { OrganizationRole } from "~/server/api/routers/router-utils/pure-utils";
import { api } from "~/trpc/react";

type ClerkOrgSwitcherProps = {
  /** Icon-only trigger — use next to marketing brand lockup in the app header. */
  compact?: boolean;
};

const ClerkOrgSwitcher = ({ compact = false }: ClerkOrgSwitcherProps) => {
  const { openCreateOrganization } = useClerk();
  const { refetchSubscription } = useSubscription();

  const { setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      pageSize: 50,
      infinite: true,
    },
  });
  const router = useRouter();
  const { user } = useUser();
  const { orgRole } = useAuth();
  const utils = api.useUtils();

  const isAdmin = orgRole === OrganizationRole.Admin;

  const [enableOrgCreateModal, setEnableOrgCreateModal] = React.useState(false);

  const { organization } = useOrganization();

  // Load every membership page so admins see the full org list.
  React.useEffect(() => {
    if (!userMemberships.hasNextPage || userMemberships.isFetching) return;
    void userMemberships.fetchNext();
  }, [
    userMemberships.hasNextPage,
    userMemberships.isFetching,
    userMemberships.fetchNext,
  ]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={compact ? "ghost" : "outline"}
            size="sm"
            className={
              compact
                ? "h-9 w-9 shrink-0 rounded-md p-0"
                : "flex w-auto shrink-0 justify-start gap-2"
            }
            title={organization?.name || "Select Organization"}
          >
            <OrganizationButton className={compact ? "h-8 w-8" : "h-5 w-5"} />
            {!compact ? (
              <span>{organization?.name || "Select Organization"}</span>
            ) : null}
            <span className="sr-only">
              {organization?.name || "Select Organization"}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="px-2 font-medium text-muted-foreground">
            My organizations
          </DropdownMenuLabel>
          {userMemberships.data?.map((mem) => (
            <DropdownMenuItem
              className="flex justify-between hover:cursor-pointer"
              key={mem.id}
              onClick={async () => {
                await setActive?.({ organization: mem.organization.id });
                await refetchSubscription();
                // Invalidate all project queries so stale data from the previous
                // org doesn't trigger getProjectById calls with the wrong session.
                void utils.project.getAllProjects.invalidate();
                void utils.project.getProjectById.invalidate();
                router.push(`/projects`);
              }}
            >
              <div className="flex items-center gap-2">
                <OrganizationAvatar
                  name={mem.organization.name}
                  imageUrl={mem.organization.imageUrl}
                  className="h-5 w-5"
                />
                <span>{mem.organization.name}</span>
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {isAdmin && (
            <DropdownMenuItem
              className="flex gap-2 hover:cursor-pointer"
              onClick={() => {
                if (user?.createOrganizationEnabled) {
                  openCreateOrganization();
                } else {
                  setEnableOrgCreateModal(true);
                }
              }}
            >
              <PlusCircle className="h-5 w-5" />
              <span>Add organization</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={enableOrgCreateModal}
        onOpenChange={setEnableOrgCreateModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-4">
              Creating organizations on EasyGPTs
            </DialogTitle>
            <DialogDescription>
              <p>
                Creating organizations on EasyGPTs is a premium feature and
                requires a paid subscription. If you are interested in creating
                an organization, please contact us at{" "}
                <b>support@easygpt.builders</b>
              </p>
              <br />
              <p>
                We offer discounts for the whitelabel partners & resellers. If
                you are interested in learning more about our pricing, please
                contact us at <b>partner@easygpt.builders</b>
              </p>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClerkOrgSwitcher;
